import React from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
	IconButton,
	Chip,
} from "@mui/material";
import { Close as CloseIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import type { Job } from "../../../../api/types";
import { isFieldVisible } from "../../../../utilities/jobValidation";
import type { ImportTableColumn } from "../../../../utilities/types";
import { useBulkEditModalViewModel } from "./BulkEditModal.vm";
import type { BulkEditModalProps } from "./BulkEditModal.types";

// Shared components
import { JobFormFields } from "../../shared/JobFormFields";
import { ValidationErrorsContext } from "../ValidationErrorsContext";
import { ColumnContext } from "../columns/standard-column-types/ColumnContext";
import { MapContainer } from "../MapContainer";

// Reuse JobDetailModal styles
import "../JobDetailModal/JobDetailModal.css";

export const BulkEditModal: React.FC<BulkEditModalProps> = (props) => {
	const { open, jobs } = props;
	const vm = useBulkEditModalViewModel(props);

	// Don't render if closed or no jobs
	if (!open || jobs.length === 0) {
		return null;
	}

	// Get current form values as a Job object for the form fields
	const currentJob = {
		id: "bulk-edit-placeholder",
		...vm.formValues,
	} as Job;

	// Cast columnContext for mobile columns
	const columnContext = vm.columnContext as unknown as ColumnContext;

	// Helper to check field visibility (use first job's context)
	const isVisible = (columnId: ImportTableColumn) =>
		isFieldVisible(columnId, {
			company: props.company,
			deal: props.allDeals.find((d) => d.id === vm.formValues.dealIds?.[0]),
			allDeals: props.allDeals,
			job: jobs[0],
			user: props.user,
			hasPointsFile: false,
			isCreatingNewSalesperson: false,
			integration: props.integration,
		});

	// Map key for forcing re-render when jobs change
	const mapKey = jobs.map((j) => j.id).join("-");

	return (
		<Dialog
			open={open}
			onClose={vm.handleClose}
			maxWidth="xl"
			fullWidth
			scroll="paper"
			className="job-detail-modal"
			aria-labelledby="bulk-edit-modal-title"
		>
			<DialogTitle id="bulk-edit-modal-title" sx={{ pr: 6 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="h6" component="span">
						Edit {jobs.length} Jobs
					</Typography>
					<Chip label={`${jobs.length} selected`} size="small" color="primary" variant="outlined" />
				</Box>
				<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
					Changes will be applied to all selected jobs. Fields with different values are shown as empty.
				</Typography>
				<IconButton
					aria-label="close"
					onClick={vm.handleClose}
					sx={{
						position: "absolute",
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			{/* Empty validation context - no validation in bulk edit mode */}
			<ValidationErrorsContext.Provider value={{}}>
				<DialogContent dividers sx={{ p: 0, display: "flex", height: "70vh" }}>
					{/* Form Fields - Left Side (Scrollable) */}
					<Box sx={{ flex: "0 0 58%", overflowY: "auto", p: 3 }}>
						<JobFormFields
							job={currentJob}
							columnContext={columnContext}
							dropdownOptions={vm.dropdownOptions}
							branches={props.branches}
							isVisible={isVisible}
							isBulkEdit={true}
							hasMixedValues={vm.hasMixedValues}
						/>
					</Box>

					{/* Map Preview - Right Side (Fixed) */}
					<Box sx={{ flex: 1, borderLeft: 1, borderColor: "divider", minWidth: 0 }}>
						<MapContainer jobs={jobs} mapKey={mapKey.length} onClose={() => {}} height="100%" />
					</Box>
				</DialogContent>
			</ValidationErrorsContext.Provider>

			<DialogActions sx={{ justifyContent: "space-between" }}>
				<Box>
					<Button startIcon={<RefreshIcon />} onClick={vm.resetForm} disabled={!vm.isDirty}>
						Reset
					</Button>
				</Box>
				<Box>
					<Button onClick={vm.handleClose} color="inherit">
						Cancel
					</Button>
					<Button variant="contained" color="primary" onClick={vm.handleApply} disabled={!vm.isDirty}>
						Apply Changes
					</Button>
				</Box>
			</DialogActions>
		</Dialog>
	);
};
